import { DisplayState } from "@/constants/state";
import { useCallback, useRef, useState } from "react";

export type TAsyncStatus = "idle" | "pending" | "complete" | "error";
export const transformAsyncStatusToDisplayState = (asyncStatus: TAsyncStatus): DisplayState => {
  const MAP: Record<TAsyncStatus, DisplayState> = {
    idle: DisplayState.READY,
    complete: DisplayState.COMPLETE,
    pending: DisplayState.PROCESSING,
    error: DisplayState.ERROR,
  };

  return MAP[asyncStatus];
};

export const useAsyncAction = <TArgs extends unknown[], TResult>(
  asyncFn: (...args: TArgs) => Promise<TResult>,
): [TAsyncStatus, (...args: TArgs) => Promise<TResult>] => {

  const [ status, setStatus ] = useState<TAsyncStatus>("idle");

  // Keep a ref to the latest asyncFn so `run` can stay referentially stable
  // across renders while still invoking the most recent closure. Call sites
  // almost always pass an inline `async () => {}` (a new identity every render);
  // depending on that directly would make `run` change every render and cause
  // effects that list it as a dependency to loop.
  const asyncFnRef = useRef(asyncFn);
  asyncFnRef.current = asyncFn;

  const run = useCallback(
    async (...args: TArgs): Promise<TResult> => {
      setStatus("pending");
      try {
        const result = await asyncFnRef.current(...args);
        setStatus("complete");
        return result;
      } catch (e) {
        setStatus("error");
        throw e;
      }
    },
    [],
  );

  return [ status, run ];

};
